import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {
  getAudioSettings,
  pauseGameAudio,
  playGameSound,
  resumeGameAudio,
  setAudioSetting,
  subscribeAudioSettings,
} from '../audio/gameAudio';
import { designTokens } from '../theme/theme';

export function GameAudioControls({ docked = false }) {
  const [settings, setSettings] = useState(getAudioSettings);
  const [audioUnavailable, setAudioUnavailable] = useState(false);

  useEffect(() => subscribeAudioSettings(setSettings), []);
  useEffect(() => () => pauseGameAudio(), []);

  useEffect(() => {
    const unlockFromGesture = async (event) => {
      if (!settings.soundEffects && !settings.music) return;
      const available = await resumeGameAudio();
      if (!available) setAudioUnavailable(true);

      const target = event.target;
      if (settings.soundEffects && target instanceof Element &&
          target.closest('button,[role="button"]') &&
          !target.closest('[data-game-audio-control]')) {
        playGameSound('tap');
      }
    };
    document.addEventListener('pointerdown', unlockFromGesture, true);
    return () => document.removeEventListener('pointerdown', unlockFromGesture, true);
  }, [settings.music, settings.soundEffects]);

  const toggleSetting = async (key) => {
    const nextValue = !settings[key];
    if (nextValue) {
      const available = await resumeGameAudio();
      if (!available) {
        setAudioUnavailable(true);
        return;
      }
      setAudioUnavailable(false);
    }
    setAudioSetting(key, nextValue);
  };

  return (
    <Box data-game-audio-control sx={{
      position: docked ? 'relative' : 'fixed',
      left: docked ? 'auto' : 'max(8px, env(safe-area-inset-left))',
      top: docked ? 'auto' : 'max(8px, env(safe-area-inset-top))',
      zIndex: docked ? 'auto' : 10001,
      display: 'flex', gap: 0.2, p: docked ? 0 : 0.35,
      borderRadius: docked ? 0 : '12px',
      bgcolor: docked ? 'transparent' : 'rgba(13,17,23,0.9)',
      border: docked ? 0 : '1px solid rgba(255,255,255,0.16)',
      backdropFilter: docked ? 'none' : 'blur(10px)',
    }}>
      <Tooltip title={settings.music ? 'Turn background music off' : 'Turn background music on'}>
        <IconButton
          data-game-audio-control
          aria-label={settings.music ? 'Turn background music off' : 'Turn background music on'}
          aria-pressed={settings.music}
          onClick={() => toggleSetting('music')}
          size="small"
          sx={{ color: settings.music ? designTokens.colors.primary : designTokens.colors.textSecondary, minWidth: 36, minHeight: 36 }}
        >
          {settings.music ? <MusicNoteIcon fontSize="small" /> : <MusicOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={settings.soundEffects ? 'Turn sound effects off' : 'Turn sound effects on'}>
        <IconButton
          data-game-audio-control
          aria-label={settings.soundEffects ? 'Turn sound effects off' : 'Turn sound effects on'}
          aria-pressed={settings.soundEffects}
          onClick={() => toggleSetting('soundEffects')}
          size="small"
          sx={{ color: settings.soundEffects ? designTokens.colors.success : designTokens.colors.textSecondary, minWidth: 36, minHeight: 36 }}
        >
          {settings.soundEffects ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      {audioUnavailable && (
        <Box role="status" sx={{
          position: 'absolute', left: 0,
          bottom: 'calc(100% + 8px)', whiteSpace: 'nowrap',
          p: 0.75, borderRadius: 1, color: '#fecaca', bgcolor: 'var(--app-surface)', fontSize: '0.72rem' }}>
          Audio is unavailable in this browser.
        </Box>
      )}
    </Box>
  );
}
